import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { authAPI } from '../services/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: () => void;
  logout: () => void;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('access_token');
      const savedUser = localStorage.getItem('user');
      
      console.log('AuthContext initialization:', {
        hasToken: !!token,
        hasUser: !!savedUser,
        userAgent: navigator.userAgent
      });
      
      if (token && savedUser) {
        try {
          const userData = JSON.parse(savedUser);
          console.log('AuthContext - Setting user data:', userData);
          setUser(userData);
          
          // For mobile browsers, be more lenient with token verification
          const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
          
          if (isMobile) {
            // On mobile, skip token verification for now to avoid redirect loops
            // The token will be verified when making actual API calls
            console.log('Mobile browser detected - skipping initial token verification');
          } else {
            // On desktop, verify token is still valid
            console.log('Desktop browser - verifying token');
            await authAPI.getCurrentUser();
            console.log('Desktop browser - token verification successful');
          }
        } catch (error) {
          console.error('Auth initialization error:', error);
          // Only clear storage if it's a JSON parsing error or desktop verification failed
          if (!savedUser || error instanceof SyntaxError) {
            console.log('Clearing auth storage due to error');
            localStorage.removeItem('access_token');
            localStorage.removeItem('user');
            setUser(null);
          }
        }
      } else {
        console.log('No auth data found in localStorage');
      }
      console.log('AuthContext - Setting loading to false');
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = () => {
    authAPI.login();
  };

  const logout = () => {
    authAPI.logout();
  };

  const isAdmin = user?.role === 'admin';

  const value = {
    user,
    loading,
    login,
    logout,
    isAdmin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
